import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';
import SchoolIcon from '@mui/icons-material/School';
import axiosInstance from '../../helpers/axios';
import { useInstituteOptions } from '../FibeCommissionBilling/hooks/useInstituteOptions';

// Candidate roles for "Zone Head" — an admin or someone already managing at the zone level.
const ZONE_HEAD_ROLES = ['admin', 'zonal_processing', 'zonal_collection'];

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function ZoneTableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export default function ZoneManagementTab() {
    const [zones, setZones] = useState([]);
    const [zoneHeadOptions, setZoneHeadOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const { institutes, refetch: refetchInstitutes } = useInstituteOptions();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [headUserId, setHeadUserId] = useState('');
    const [assignedInstitutes, setAssignedInstitutes] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [modalError, setModalError] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const loadZones = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        axiosInstance
            .get('/admin-settings/zones')
            .then((response) => setZones(response.data.data))
            .catch(() => setErrorMessage('Could not load zones.'))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadZones();
        axiosInstance
            .get('/admin-settings/users', { params: { role: ZONE_HEAD_ROLES.join(','), limit: 100 } })
            .then((response) => setZoneHeadOptions(response.data.data.users))
            .catch(() => setZoneHeadOptions([]));
    }, [loadZones]);

    const zoneHeadNameById = new Map(zoneHeadOptions.map((u) => [u.id, u.name]));
    const totalZones = zones.length;
    const totalInstitutesAssigned = zones.reduce((sum, z) => sum + (z.instituteCount || 0), 0);

    const openCreateModal = () => {
        setEditingZone(null);
        setName('');
        setDescription('');
        setHeadUserId('');
        setAssignedInstitutes([]);
        setModalError(null);
        setModalOpen(true);
    };

    const openEditModal = (zone) => {
        setEditingZone(zone);
        setName(zone.name || '');
        setDescription(zone.description || '');
        setHeadUserId(zone.headUserId || '');
        setAssignedInstitutes(institutes.filter((i) => i.zoneId === zone.id).map((i) => i.id));
        setModalError(null);
        setModalOpen(true);
    };

    const handleSave = () => {
        setIsSaving(true);
        setModalError(null);
        const payload = { name, description: description || undefined, headUserId: headUserId || undefined };
        const request = editingZone
            ? axiosInstance.put(`/admin-settings/zones/${editingZone.id}`, payload)
            : axiosInstance.post('/admin-settings/zones', payload);

        request
            .then((response) => {
                const zoneId = editingZone ? editingZone.id : response.data.data.id;
                return axiosInstance.put(`/admin-settings/zones/${zoneId}/institutes`, { instituteIds: assignedInstitutes });
            })
            .then(() => {
                setModalOpen(false);
                setSnackbar({
                    open: true,
                    message: editingZone ? 'Zone updated successfully' : 'Zone created successfully',
                    severity: 'success',
                });
                loadZones();
                refetchInstitutes();
            })
            .catch((error) => {
                setModalError(error?.response?.data?.message || 'Could not save zone.');
            })
            .finally(() => setIsSaving(false));
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        setDeleteError(null);
        axiosInstance
            .delete(`/admin-settings/zones/${deleteTarget.id}`)
            .then(() => {
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Zone deleted successfully', severity: 'success' });
                loadZones();
            })
            .catch((error) => {
                if (error?.response?.status === 409) {
                    setDeleteError('Cannot delete: zone has assigned institutes or users. Reassign them first.');
                } else {
                    setDeleteError(error?.response?.data?.message || 'Could not delete zone.');
                }
            });
    };

    const canSave = name.trim().length > 0 && name.length <= 100;

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderRadius: '14px',
                    border: '1.5px solid #d9760933', boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <MapIcon sx={{ fontSize: 22, color: '#d97706' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Total Zones</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{totalZones}</Typography>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderRadius: '14px',
                    border: '1.5px solid #D3202833', boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#fff0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <SchoolIcon sx={{ fontSize: 22, color: '#D32028' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Total Institutes Assigned</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{totalInstitutesAssigned}</Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <Button variant="contained" onClick={openCreateModal} sx={{ color: '#fff' }}>Create Zone</Button>
            </Box>

            {errorMessage && <Alert severity="error" sx={{ marginBottom: '12px' }}>{errorMessage}</Alert>}

            <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#F8F8F8' }}>
                            <TableRow>
                                <TableCell>Zone Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Zone Head</TableCell>
                                <TableCell>Institutes</TableCell>
                                <TableCell>Created Date</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <ZoneTableSkeleton />
                        ) : zones.length === 0 ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', padding: '32px' }}>
                                        No zones created yet. Create your first zone to start organizing institutes.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        ) : (
                            <TableBody>
                                {zones.map((zone) => (
                                    <TableRow key={zone.id} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{zone.name}</TableCell>
                                        <TableCell>{zone.description || '—'}</TableCell>
                                        <TableCell>{zone.headUserId ? (zoneHeadNameById.get(zone.headUserId) || zone.headUserId) : '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={`${zone.instituteCount || 0} institutes`} />
                                        </TableCell>
                                        <TableCell>{formatDate(zone.createdAt)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => openEditModal(zone)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => { setDeleteTarget(zone); setDeleteError(null); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="sm"
                sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
                PaperProps={{ sx: { marginTop: '32px' } }}
            >
                <DialogTitle>{editingZone ? 'Edit Zone' : 'Create Zone'}</DialogTitle>
                <DialogContent>
                    {modalError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{modalError}</Alert>}
                    <TextField
                        fullWidth
                        label="Name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value.slice(0, 100))}
                        inputProps={{ maxLength: 100 }}
                        sx={{ marginBottom: '16px', marginTop: '8px' }}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value.slice(0, 255))}
                        inputProps={{ maxLength: 255 }}
                        sx={{ marginBottom: '16px' }}
                    />
                    <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                        <InputLabel shrink>Zone Head</InputLabel>
                        <Select
                            displayEmpty
                            label="Zone Head"
                            value={headUserId}
                            onChange={(e) => setHeadUserId(e.target.value)}
                            MenuProps={{
                                variant: 'menu',
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                PaperProps: { style: { maxHeight: 250, width: 260, minWidth: 260 } },
                            }}
                        >
                            <MenuItem value="">No zone head</MenuItem>
                            {zoneHeadOptions.map((u) => (
                                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Autocomplete
                        multiple
                        options={institutes}
                        getOptionLabel={(option) => option.brandName || option.id}
                        value={institutes.filter((i) => assignedInstitutes.includes(i.id))}
                        onChange={(e, value) => setAssignedInstitutes(value.map((v) => v.id))}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip label={option.brandName} size="small" {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} label="Assigned Institutes" />}
                        ListboxProps={{ style: { maxHeight: 250 } }}
                        slotProps={{
                            popper: {
                                placement: 'bottom-start',
                                modifiers: [{ name: 'flip', enabled: false }],
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving} sx={{ color: '#fff' }}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete Zone</DialogTitle>
                <DialogContent>
                    {deleteError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{deleteError}</Alert>}
                    Are you sure you want to delete zone {deleteTarget?.name}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
